import { useCreateSquawkMutation } from "@common/api/quoting/squawk"
import { Button } from "@common/components/ui/button"
import { useMaxWidth } from "@common/hooks/use-mobile"
import {
  useHasPermission,
  useHasRolePermission,
} from "@common/hooks/use-permissions"
import { handleError } from "@common/utils"
import {
  createFileRoute,
  type LinkOptions,
  linkOptions,
  Outlet,
  useMatches,
  useNavigate,
} from "@tanstack/react-router"
import { Suspense, useMemo, useState } from "react"
import { ColumnChips } from "@/components/layout/column-chips"
import { DETAIL_SHEET_BREAKPOINT } from "@/components/layout/detail-panel"
import {
  type Display,
  DisplayOptions,
} from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import useCustomToast from "@/hooks/use-custom-toast"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { AttentionStrip } from "./-components/attention-strip"
import { SquawkFilterPanel } from "./-components/filter-panel"
import { SquawkListContext } from "./-components/squawk-list"
import { type Picked, SquawkTable } from "./-components/squawk-table"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  FIXED_COLUMNS,
} from "./-lib/columns"
import {
  hasSquawkFilters,
  NO_SQUAWK_FILTERS,
  squawkFilterConfigs,
} from "./-lib/filters"
import { GROUPING_LABELS, GROUPINGS, type Grouping } from "./-lib/grouping"
import {
  columnsSearch,
  displayOf,
  ensureSquawks,
  type SquawksDisplay,
  type SquawksSearch,
  sortSearch,
  squawkOptionsParams,
  squawkPagesParams,
  squawksSearchSchema,
  TABS,
  tabLabel,
} from "./-lib/search"
import { NEW_SQUAWK_DESCRIPTION } from "./-lib/status"

const CLOSE_SQUAWK: LinkOptions = linkOptions({
  to: "/quoting/squawks",
  search: true,
})

function openSquawk(squawkId: string): LinkOptions {
  return linkOptions({
    to: "/quoting/squawks/$squawkId",
    params: { squawkId },
    search: true,
  })
}

export const Route = createFileRoute("/_layout/quoting_/squawks")({
  staticData: { breadcrumb: "Squawks" },
  validateSearch: (search) => squawksSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    pages: squawkPagesParams(search),
    grouped: Boolean(displayOf(search).group),
  }),
  loader: ({ context: { queryClient }, deps }) =>
    ensureSquawks(queryClient, deps.pages, deps.grouped),
  component: SquawksPage,
})

function useUpdateSearch(): (patch: Partial<SquawksSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function SquawksPage(): React.ReactElement {
  const search = Route.useSearch()
  const display = displayOf(search)
  const update = useUpdateSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)
  const open = useMatches({
    select: (matches) =>
      matches.some((match) => match.staticData.detailSidebar),
  })
  const compact = useMaxWidth(DETAIL_SHEET_BREAKPOINT)
  const [picked, setPicked] = useState<Picked>(new Set())
  const list = useMemo(
    () => ({
      search,
      params: squawkPagesParams(search),
      options: squawkOptionsParams(search),
      open: openSquawk,
      close: CLOSE_SQUAWK,
    }),
    [search],
  )

  return (
    <SquawkListContext.Provider value={list}>
      <ListPage
        toolbar={<Toolbar display={display} hidden={fit.hidden} />}
        strip={
          <Suspense>
            <AttentionStrip />
          </Suspense>
        }
        pane={open && !compact ? "split" : "closed"}
        detail={<Outlet />}
        scrollRef={ref}
      >
        <ActiveFilters />
        <Suspense>
          <SquawkTable
            picked={picked}
            onPick={setPicked}
            columns={fit.shown}
            onSort={(column) => update(sortSearch(display, column))}
            onClear={() => update({ ...NO_SQUAWK_FILTERS, search: undefined })}
          />
        </Suspense>
      </ListPage>
    </SquawkListContext.Provider>
  )
}

function Toolbar({
  display,
  hidden,
}: {
  display: SquawksDisplay
  hidden: Column[]
}): React.ReactElement {
  const values = Route.useSearch()
  const { tab = "all", search = "" } = values
  const update = useUpdateSearch()
  const canSubscribe = useHasPermission("work_package:subscribe")
  const canCreate = useHasPermission("squawk:create")
  const actsForOthers = useHasRolePermission("users:act_as")
  const tabs = TABS.filter((value) => value !== "subscribed" || canSubscribe)

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={tabs}
          tab={tab}
          label={tabLabel}
          onSelect={(next) =>
            update({ tab: next === "all" ? undefined : next })
          }
        />
      }
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search squawks…"
          />
          <FilterButton
            active={hasSquawkFilters(values)}
            configs={squawkFilterConfigs}
            onChange={update}
          />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            group={display.group}
            subgroup={display.subgroup}
            onChange={(next) => update(groupingSearch(next))}
          >
            <ColumnChips
              options={COLUMNS}
              labels={COLUMN_LABELS}
              fixed={FIXED_COLUMNS}
              columns={display.columns}
              hidden={hidden}
              onChange={(next) => update(columnsSearch(next))}
            />
          </DisplayOptions>
          {canCreate && !actsForOthers && <NewSquawkButton />}
        </>
      }
    />
  )
}

function NewSquawkButton(): React.ReactElement {
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useCreateSquawkMutation()

  const create = () =>
    mutation.mutate(
      {
        squawkUpsert: { description: NEW_SQUAWK_DESCRIPTION, status: "draft" },
      },
      {
        onSuccess: (created) => navigate(openSquawk(created.id)),
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <Button size="sm" disabled={mutation.isPending} onClick={create}>
      New squawk
    </Button>
  )
}

function groupingSearch({
  group,
  subgroup,
}: Display<Grouping>): Partial<SquawksSearch> {
  return {
    layout: undefined,
    group: group === "work_order" ? undefined : (group ?? "none"),
    subgroup,
  }
}

function ActiveFilters(): React.ReactElement {
  const { status, severity, work_order, customer, reporter, ata } =
    Route.useSearch()
  const update = useUpdateSearch()

  return (
    <SquawkFilterPanel
      values={{ status, severity, work_order, customer, reporter, ata }}
      onChange={update}
    />
  )
}
